import React, { useEffect, useState, useMemo } from "react";
import { createApiClient } from "../../Network/ApiClient";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../../components/ui/toaster";
import { 
  HeartPulse, 
  Stethoscope, 
  ShieldCheck, 
  Sliders, 
  ChevronRight,
  Loader2,
  LayoutGrid,
  PieChart as PieChartIcon,
  BarChart3,
  CheckCircle,
  XCircle,
  SearchX,
  Lock,
  Settings
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { invalidateFeatureCache } from "../../hooks/useFeatureStatus";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent
} from "../../components/ui/chart";
import { 
  Bar, 
  BarChart, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Pie, 
  PieChart, 
  Cell,
  LabelList
} from "recharts";
import { cn } from "../../lib/utils";

const api = createApiClient("features/");

const groups = [
  { 
    id: "activity",
    title: "Activity configuration", 
    icon: HeartPulse, 
    color: "text-emerald-600", 
    bgColor: "bg-emerald-50",
    description: "Manage pre-approvals, age/gender restrictions, and frequency controls.",
    keys: [
      "ACTIVITY_PRE_APPROVAL","ACTIVITY_AGE_GENDER","ACTIVITY_NOT_ALLOWED",
      "ACTIVITY_ALLOWED","ACTIVITY_INCLUDED_ACTIVITY","ACTIVITY_NOT_ICD",
      "ACTIVITY_ALLOWED_ICD","ACTIVITY_NOT_COVERED","FREQUENCY_CONTROL"
    ] 
  },
  { 
    id: "icd",
    title: "ICD configuration", 
    icon: Stethoscope, 
    color: "text-blue-600", 
    bgColor: "bg-blue-50",
    description: "Control ICD allowlists, coverage, and diagnostic relationships.",
    keys: ["ICD_NOT_ALLOWED","ICD_ALLOWED","ICD_NOT_COVERED","RELATION_AGE_GENDER","PRINCIPAL_DIAGNOSED"] 
  },
  { 
    id: "benefit",
    title: "Benefit configuration", 
    icon: ShieldCheck, 
    color: "text-indigo-600", 
    bgColor: "bg-indigo-50",
    description: "Define benefit groups and individual benefit permissions.",
    keys: [ "BENEFIT"] 
  },
  { 
    id: "configure",
    title: "Configure", 
    icon: Settings, 
    color: "text-amber-600", 
    bgColor: "bg-amber-50",
    description: "Control CRUD and validation for all Configuration Levels (Policy, Insurance Plan, Network, Provider, Patient, Doctor, VIP, Specialty).",
    keys: ["CONFIG_LEVEL"] 
  },
];

const labels: Record<string, string> = {
  ACTIVITY_PRE_APPROVAL:      "Pre-approval",
  ACTIVITY_AGE_GENDER:        "Age & gender",
  ACTIVITY_NOT_ALLOWED:       "Not allowed together",
  ACTIVITY_ALLOWED:           "Allowed together",
  ACTIVITY_INCLUDED_ACTIVITY: "Activity Bundle",
  ACTIVITY_NOT_ICD:           "Not allowed with ICD",
  ACTIVITY_ALLOWED_ICD:       "Must come with ICD",
  ACTIVITY_NOT_COVERED:       "Not covered",
  FREQUENCY_CONTROL:          "Frequency control",
  ICD_NOT_ALLOWED:            "ICD not allowed",
  ICD_ALLOWED:                "ICD must come",
  ICD_NOT_COVERED:            "ICD not covered",
  RELATION_AGE_GENDER:        "Age & gender",
  PRINCIPAL_DIAGNOSED:        "Principal diagnosed",
  BENEFIT:                    "Benefit",
  CLAIM_VALIDATION:           "Claim validation",
  BULK_CLAIM_VALIDATION:      "Bulk claim validation",
  VALIDATION_HISTORY:         "Validation history",
  CONFIG_LEVEL:                "Configuration Levels",
};

const chartConfig = {
  active:   { label: "Active Features",   color: "hsl(var(--primary))" },
  inactive: { label: "Inactive Features", color: "hsl(var(--muted-foreground))" },
} satisfies any;

const FeatureControl: React.FC = () => {
  const [features, setFeatures]       = useState<any[]>([]);
  const [loadingKey, setLoadingKey]   = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const { toast } = useToast();

  // ── Role check ────────────────────────────────────────────────────────────
  const storedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); }
    catch { return {}; }
  }, []);
  const currentRole: string = storedUser?.role ?? "";
  const canToggle = currentRole === "system_admin";

  useEffect(() => {
    api.get("")
      .then(res => setFeatures(res.data.features || []))
      .catch(() => {
        toast({ title: "Error", description: "Failed to load features", variant: "destructive" });
      })
      .finally(() => setPageLoading(false));
  }, []);

  const toggle = async (key: string, current: boolean) => {
    if (!canToggle) return; // ← only system_admin can toggle
    setLoadingKey(key);
    try {
      await api.put("update", { data: { featureKey: key, isEnabled: !current } });
      setFeatures(prev =>
        prev.map(f => f.feature_key === key ? { ...f, is_enabled: !current } : f)
      );
      toast({ title: "Success", description: `${labels[key]} ${!current ? "enabled" : "disabled"}.` });
      invalidateFeatureCache();
    } catch {
      toast({ title: "Error", description: "Failed to update feature", variant: "destructive" });
    } finally {
      setLoadingKey(null);
    }
  };

  const getF = (key: string) => features.find(f => f.feature_key === key);

  const filteredGroupsList = useMemo(() => {
    if (!searchQuery) return groups;
    return groups.filter(g =>
      g.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.keys.some(k => labels[k]?.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [searchQuery]);

  const displayedFeatures = useMemo(() => {
    if (activeGroup === "all" && !searchQuery) return [];
    let result: string[] = activeGroup === "all"
      ? groups.flatMap(g => g.keys)
      : groups.find(g => g.id === activeGroup)?.keys || [];
    if (searchQuery)
      result = result.filter(k => labels[k]?.toLowerCase().includes(searchQuery.toLowerCase()));
    return result;
  }, [activeGroup, searchQuery]);

  const barData = useMemo(() => {
    return groups.map(g => ({
      name: g.title.split(" ")[0],
      active:   g.keys.filter(k => getF(k)?.is_enabled).length,
      inactive: g.keys.length - g.keys.filter(k => getF(k)?.is_enabled).length,
    }));
  }, [features]);

  const pieData = useMemo(() => {
    const active = features.filter(f => f.is_enabled).length;
    return [
      { name: "Active",   value: active,                   fill: "hsl(var(--primary))" },
      { name: "Inactive", value: features.length - active, fill: "hsl(var(--muted))"   },
    ];
  }, [features]);

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div className="flex h-[calc(100vh-64px)] bg-slate-50 overflow-hidden">
        <div className="w-80 border-r bg-white p-6 space-y-6">
          <Skeleton className="h-10 w-full rounded-md" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}
          </div>
        </div>
        <div className="flex-1 p-8 pt-20 space-y-8">
          <div className="flex justify-between items-center">
            <Skeleton className="h-10 w-64 rounded-md" />
            <Skeleton className="h-10 w-32 rounded-full" />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <Skeleton className="h-80 w-full rounded-xl" />
            <Skeleton className="h-80 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  const currentGroupData = groups.find(g => g.id === activeGroup);
  const enabledCount     = features.filter(f => f.is_enabled).length;

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-64px)] bg-[#F8FAFC] overflow-hidden font-sans">
      <Toaster />

      {/* ── Sidebar ── */}
      <aside className="w-80 bg-white border-r border-slate-200 flex flex-col shrink-0">
        <div className="p-6 pb-4">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="bg-primary p-2 rounded-lg shadow-sm">
              <Sliders className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 tracking-tight">Features</h2>
            {/* Read-only badge for non-system-admin */}
            {!canToggle && (
              <Badge variant="secondary" className="ml-auto text-[9px] font-black tracking-widest bg-amber-50 text-amber-600 border border-amber-200 px-2 gap-1 flex items-center">
                <Lock className="w-2.5 h-2.5" />
                View only
              </Badge>
            )}
          </div>

          <div className="relative flex items-center group">
            <Input
              placeholder="Search features..."
              className="pl-12 pr-4 bg-slate-50 border-slate-200 h-12 focus-visible:ring-2 focus-visible:ring-primary/10 focus-visible:border-primary transition-all rounded-2xl text-sm w-full"
              style={{ paddingLeft: "48px" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <ScrollArea className="flex-1 px-4 py-2">
          <div className="space-y-1">
            <button
              onClick={() => setActiveGroup("all")}
              className={cn(
                "w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 group",
                activeGroup === "all"
                  ? "bg-slate-900 text-white shadow-lg shadow-slate-200"
                  : "text-slate-600 hover:bg-slate-50"
              )}
            >
              <div className="flex items-center gap-3">
                <LayoutGrid className={cn("w-4 h-4", activeGroup === "all" ? "text-white" : "text-slate-400")} />
                <span className="text-sm font-bold tracking-tight">System Overview</span>
              </div>
              <Badge
                variant={activeGroup === "all" ? "outline" : "secondary"}
                className={cn(
                  "text-[10px] h-5 min-w-[20px] flex items-center justify-center",
                  activeGroup === "all" ? "text-white border-slate-700 bg-slate-800" : "bg-slate-100"
                )}
              >
                {enabledCount}
              </Badge>
            </button>

            {filteredGroupsList.map((g) => {
              const Icon     = g.icon;
              const isActive = activeGroup === g.id;
              return (
                <button
                  key={g.id}
                  onClick={() => setActiveGroup(g.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 group",
                    isActive
                      ? "bg-primary text-white shadow-lg shadow-primary/20"
                      : "text-slate-600 hover:bg-slate-50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn("p-1.5 rounded-lg transition-colors", isActive ? "bg-white/20" : g.bgColor)}>
                      <Icon className={cn("w-3.5 h-3.5", isActive ? "text-white" : g.color)} />
                    </div>
                    <span className="text-sm font-bold tracking-tight">{g.title.split(" ")[0]}</span>
                  </div>
                  <ChevronRight className={cn(
                    "w-3.5 h-3.5 opacity-40 transition-transform",
                    isActive ? "translate-x-0.5 opacity-100" : "group-hover:translate-x-0.5"
                  )} />
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </aside>

      {/* ── Main content ── */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-10 pt-32 pb-24">

          {/* Header */}
          <header className="mb-14">
            <div className="flex items-start justify-between mb-4">
              <div className="animate-in fade-in slide-in-from-left-4 duration-500">
                <h1 className="text-4xl font-black text-slate-900 tracking-tighter mb-3">
                  {searchQuery
                    ? `Search Results for "${searchQuery}"`
                    : activeGroup === "all" ? "Dashboard Overview" : currentGroupData?.title}
                </h1>
                <p className="text-slate-500 text-base max-w-xl leading-relaxed font-medium">
                  {searchQuery
                    ? `Showing ${displayedFeatures.length} matching features across all categories.`
                    : activeGroup === "all"
                      ? "Advanced analytics and control panel for all system-wide features and security modules."
                      : currentGroupData?.description}
                </p>
              </div>
              <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right-4 duration-500">
                <Badge variant="outline" className="px-4 py-2 border-slate-200 bg-white text-slate-600 font-bold rounded-xl shadow-sm">
                  {enabledCount} of {features.length} Enabled
                </Badge>
              </div>
            </div>

            {/* Read-only notice banner */}
            {!canToggle && (
              <div className="flex items-center gap-3 mt-6 px-5 py-3.5 bg-amber-50 border border-amber-200 rounded-2xl animate-in fade-in duration-500">
                <div className="p-1.5 bg-amber-100 rounded-lg shrink-0">
                  <Lock className="w-3.5 h-3.5 text-amber-600" />
                </div>
                <p className="text-xs font-bold text-amber-700 leading-relaxed">
                  You have <span className="font-black">read-only</span> access to this panel. Only System Administrators can enable or disable features.
                </p>
              </div>
            )}
          </header>

          {/* Charts — Overview only */}
          {activeGroup === "all" && !searchQuery && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 animate-in zoom-in-95 duration-500">
              {/* Bar chart */}
              <Card className="border-none shadow-xl shadow-slate-200/50 rounded-[2rem] p-8">
                <CardHeader className="px-0 pt-0 pb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <BarChart3 className="w-4 h-4 text-primary" />
                    <CardTitle className="text-sm font-black uppercase tracking-widest text-slate-400">
                      Category Distribution
                    </CardTitle>
                  </div>
                  <CardDescription>Features enabled across core system categories</CardDescription>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                  <ChartContainer config={chartConfig} className="h-[250px] w-full">
                    <BarChart data={barData} margin={{ top: 20, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 700 }}
                        dy={10}
                      />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="active" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} barSize={40}>
                        <LabelList dataKey="active" position="top" style={{ fill: "#475569", fontSize: 10, fontWeight: 800 }} />
                      </Bar>
                      <Bar dataKey="inactive" fill="hsl(var(--muted))" radius={[6, 6, 0, 0]} barSize={40} />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              {/* Pie chart */}
              <Card className="border-none shadow-xl shadow-slate-200/50 rounded-[2rem] p-8 flex flex-col items-center justify-center overflow-hidden">
                <CardHeader className="w-full px-0 pt-0 pb-6">
                  <div className="flex items-center gap-2 mb-1">
                    <PieChartIcon className="w-4 h-4 text-emerald-500" />
                    <CardTitle className="text-sm font-black uppercase tracking-widest text-slate-400">
                      Activity Split
                    </CardTitle>
                  </div>
                  <CardDescription>Overall ratio of active vs inactive modules</CardDescription>
                </CardHeader>
                <div className="relative w-full flex-1 min-h-[250px]">
                  <ChartContainer config={chartConfig} className="h-full w-full">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={8}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} stroke="none" />
                        ))}
                      </Pie>
                      <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                    </PieChart>
                  </ChartContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-3xl font-black text-slate-900">
                      {features.length > 0 ? Math.round((enabledCount / features.length) * 100) : 0}%
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active</span>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Features list */}
          {(activeGroup !== "all" || searchQuery) && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-700">
              {displayedFeatures.length > 0 ? (
                displayedFeatures.map((key) => {
                  const f         = getF(key);
                  const isOn      = f?.is_enabled ?? false;
                  const isLoading = loadingKey === key;
                  // disabled if: loading, feature not found, OR user can't toggle
                  const disabled  = isLoading || !f || !canToggle;

                  return (
                    <Card
                      key={key}
                      className={cn(
                        "group border-none transition-all duration-500 rounded-[1.5rem] overflow-hidden",
                        isOn
                          ? "bg-white shadow-xl shadow-slate-200/40"
                          : "bg-slate-50/50 border border-slate-100 shadow-none grayscale-[0.5]"
                      )}
                    >
                      <CardContent className="p-0">
                        <div className="flex items-center justify-between p-7">
                          <div className="flex items-center gap-6">
                            <div className={cn(
                              "flex items-center justify-center w-14 h-14 rounded-2xl transition-all duration-500",
                              isOn ? "bg-slate-900 shadow-xl shadow-slate-300 scale-105" : "bg-white shadow-inner"
                            )}>
                              {isOn
                                ? <CheckCircle className="w-6 h-6 text-emerald-400" />
                                : <XCircle    className="w-6 h-6 text-slate-300"    />}
                            </div>
                            <div>
                              <div className="flex items-center gap-3 mb-1">
                                <h3 className={cn(
                                  "text-lg font-black tracking-tight transition-colors duration-300",
                                  isOn ? "text-slate-900" : "text-slate-400"
                                )}>
                                  {labels[key] || key}
                                </h3>
                                <Badge
                                  variant="secondary"
                                  className={cn(
                                    "text-[9px] font-black uppercase tracking-tighter h-4 px-1.5",
                                    isOn ? "bg-emerald-500/10 text-emerald-600" : "bg-slate-200 text-slate-400"
                                  )}
                                >
                                  {isOn ? "Online" : "Paused"}
                                </Badge>
                              </div>
                              <p className="text-slate-400 text-xs font-medium tracking-tight">
                                Feature identifier:{" "}
                                <code className="bg-slate-100 px-1 py-0.5 rounded text-[10px]">{key}</code>
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-8">
                            {isLoading && (
                              <div className="flex items-center gap-2 text-primary animate-bounce">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-[10px] font-black uppercase tracking-[0.2em]">Updating</span>
                              </div>
                            )}
                            <div className={cn(
                              "relative p-2.5 rounded-2xl transition-all duration-500",
                              isOn ? "bg-emerald-50 ring-1 ring-emerald-100 shadow-inner" : "bg-slate-100"
                            )}>
                              <Switch
                                checked={isOn}
                                disabled={disabled}
                                onCheckedChange={() => toggle(key, isOn)}
                                className="data-[state=checked]:bg-emerald-500 scale-110"
                              />
                              {/* Lock icon overlay for non-system-admin */}
                              {!canToggle && (
                                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center shadow-sm">
                                  <Lock className="w-2.5 h-2.5 text-amber-600" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <div className="flex flex-col items-center justify-center py-24 text-center animate-in fade-in zoom-in-95 duration-500">
                  <div className="bg-white shadow-2xl shadow-slate-200 p-8 rounded-[2.5rem] mb-6">
                    <SearchX className="w-10 h-10 text-slate-200 stroke-[3px]" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 tracking-tight">No results found</h3>
                  <p className="text-slate-400 text-sm max-w-xs mt-2 font-medium leading-relaxed">
                    We searched through all modules but couldn't find anything matching{" "}
                    <span className="text-primary font-bold">"{searchQuery}"</span>.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Toaster />
    </div>
  );
};

export default FeatureControl;